import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { Box, Flex, Text, useDisclosure, useTheme, useToast } from '@chakra-ui/react';

import { AdElement, Datasources } from '@adflow/types';
import { useStore } from '../../hooks/useStore';
import { DATA_SOURCE_REMOVED } from '../../store';
import { CancelConfirmation } from '../CancelConfirmation';
import EditableHeading from '../EditableHeading';
import { SaveConfirmation } from '../SaveConfirmation';
import { WarningModal } from '../WarningModal';

import Button from '../../components/ThemedButton';
import {
  getPendingTemplateSave,
  registerTemplateSaveRequest,
  settleTemplateSave,
} from '../../save/saveRequest';
import { SourceConfig } from '../Editor';
import { getVariantLabel } from '../Rightsidebar/TextElementControls/utils/fontUtils';

export interface Props {
  onSave: (name: string) => Promise<void>;
  saveButtonVisible: boolean;
  sources: SourceConfig[];
}

const Topbar: FC<Props> = ({ onSave, saveButtonVisible, sources }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isWarningOpen, setWarningOpen] = useState(false);
  const [warning, setWarning] = useState<string | ReactNode>('');
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const spacingTwo = theme.space[2];
  const [showWarningForContentTemplateLink, setShowWarningForContentTemplateLink] = useState(false);

  const isInvalidElement = (el: AdElement) => {
    if (el.type === 'TEXT') {
      return el.data.content === DATA_SOURCE_REMOVED;
    }

    if (el.type === 'CASINO_ASSET') {
      return (
        !el.data.source ||
        !['SQUARE_IMAGE', 'LANDSCAPE_IMAGE', 'PORTRAIT_IMAGE'].includes(el.data.source.toString())
      );
    }

    return false;
  };

  const {
    name,
    setName,
    bgColour,
    setBgColour,
    brand,
    getInvalidFontSelections,
    getInvalidElementContentMappings,
    validateContentTemplateLinks,
    validateElementContents,
    isLegacyEventTemplate,
    isAnyElementContentInvalid,
    isMarketSelected,
    businessEntityId,
    dataSources,
    workingRevision,
  } = useStore(
    (state) => ({
      name: state.name,
      brand: state.brand,
      setName: state.setName,
      bgColour: state.bgColour,
      setBgColour: state.setBgColour,
      getInvalidFontSelections: state.getInvalidFontSelections,
      getInvalidElementContentMappings: state.getInvalidElementContentMappings,
      validateContentTemplateLinks: state.validateContentTemplateLinks,
      validateElementContents: state.validateElementContents,
      isLegacyEventTemplate: state.dataSources.some((ds) => ds.type === 'EVENT'),
      isAnyElementContentInvalid: state.elements.some(isInvalidElement),
      isMarketSelected: state.dataSources.some((ds) => Datasources.isMarketDataType(ds.type)),
      businessEntityId: state.businessEntityId,
      dataSources: state.dataSources,
      workingRevision: state.workingRevision ?? 0,
    }),
    shallow,
  );

  // repass the query received to the next page
  const location = useLocation();
  const query = useMemo(() => {
    return location.search ? `${location.search}` : '';
  }, [location]);

  const workingRevisionRef = useRef(workingRevision);
  workingRevisionRef.current = workingRevision;
  const confirmingSaveRef = useRef(false);
  const blockingMessageRef = useRef<string | null>(null);
  const openSaveRef = useRef<() => 'dialog' | 'blocked'>(() => 'dialog');
  const closeSaveRef = useRef<() => void>(() => undefined);
  const confirmSaveRef = useRef<() => void>(() => undefined);
  closeSaveRef.current = () => {
    blockingMessageRef.current = null;
    setConfirmationOpen(false);
    setWarningOpen(false);
    setShowWarningForContentTemplateLink(false);
  };

  useEffect(() => {
    return registerTemplateSaveRequest(
      () => openSaveRef.current(),
      () => closeSaveRef.current(),
      () => confirmSaveRef.current(),
    );
  }, []);

  const handleSaveConfirm = useCallback(async () => {
    if (onSave) {
      const pendingSave = getPendingTemplateSave();
      if (pendingSave && workingRevisionRef.current !== pendingSave.expectedWorkingRevision) {
        settleTemplateSave({ status: 'stale' });
        setConfirmationOpen(false);
        setShowWarningForContentTemplateLink(false);
        return;
      }
      confirmingSaveRef.current = true;
      setIsLoading(true);
      try {
        await onSave(name);
        settleTemplateSave({ status: 'saved' });
        navigate(`/${query}`);
      } catch {
        settleTemplateSave({ status: 'failed', message: 'Something went really wrong' });
        toast({
          title: 'Save failed',
          description: 'Something went really wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        confirmingSaveRef.current = false;
        setIsLoading(false);
        setConfirmationOpen(false);
        setShowWarningForContentTemplateLink(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSave, name, toast, navigate]);
  confirmSaveRef.current = () => {
    void handleSaveConfirm();
  };

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
    setShowWarningForContentTemplateLink(false);
    if (!confirmingSaveRef.current) {
      settleTemplateSave({ status: 'cancelled' });
    }
  };

  const handleWarningClose = () => {
    const message = blockingMessageRef.current;
    blockingMessageRef.current = null;
    setWarningOpen(false);
    setWarning('');
    if (message) {
      settleTemplateSave({ status: 'blocked', message });
    }
  };

  const showBlockingWarning = (message: string, body?: ReactNode) => {
    blockingMessageRef.current = message;
    setWarning(body ?? message);
    setWarningOpen(true);
  };

  /**
   * Return the source selected by the user, setting the first one if
   * does not find any template or market type related to any source available
   */
  const selectedSource = useMemo(() => {
    // for some reason, the dataSources come with id and type only when first loaded.
    // After we change something in the "Configure Datasource" section, it fills
    // the "dataType" field
    const selectedSource =
      sources.find(
        (s) =>
          s.templateTypes.some((t) => dataSources.some((ds) => ds.type === t)) ||
          s.marketTypes.some((m) => dataSources.some((ds) => ds.type === m)),
      ) || sources[0];

    return selectedSource;
  }, [dataSources, sources]);

  /** retrieve the information if the marketing is required for that source.  */
  const marketTypeRequired = useMemo(
    () => !selectedSource.acceptEmptyMarkets,
    [selectedSource.acceptEmptyMarkets],
  );

  const buildInvalidContentMappingWarning = () => {
    const invalidMappings = getInvalidElementContentMappings();
    if (!invalidMappings.length) return null;

    const previewItems = invalidMappings.slice(0, 3);
    const suffix =
      invalidMappings.length > 3 ? `and ${invalidMappings.length - 3} more affected mappings` : '';

    return (
      <Box>
        <Text mb={2}>
          Custom asset mappings do not match the selected data source. Use{' '}
          <Text as="span" fontFamily="mono">
            HOME_COMPETITOR_ID
          </Text>{' '}
          /{' '}
          <Text as="span" fontFamily="mono">
            AWAY_COMPETITOR_ID
          </Text>{' '}
          for Sports, or{' '}
          <Text as="span" fontFamily="mono">
            TEAM_HOME_COMPETITOR_ID
          </Text>{' '}
          /{' '}
          <Text as="span" fontFamily="mono">
            TEAM_AWAY_COMPETITOR_ID
          </Text>{' '}
          for Soccer Sports Moments.
        </Text>
        <Box as="ul" pl={4}>
          {previewItems.map((item) => (
            <Box as="li" key={`${item.elementId}:${item.sourceId}:${item.dataPointName}`}>
              <Text as="span" fontWeight="bold">
                {item.elementName}
              </Text>{' '}
              uses{' '}
              <Text as="span" fontFamily="mono">
                {item.dataPointName}
              </Text>
            </Box>
          ))}
          {suffix && (
            <Box as="li" listStyleType="none" mt={1}>
              <Text fontStyle="italic">{suffix}</Text>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const handleSaveConfirmOpen = (): 'dialog' | 'blocked' => {
    if (isLegacyEventTemplate) {
      showBlockingWarning(
        'This template needs to be updated. Please update the Datasources and the content of the elements before saving.',
      );
      return 'blocked';
    }
    console.log('source', {
      dataSources,
      marketTypeRequired,
      selectedSource,
    });
    if (marketTypeRequired && !isMarketSelected) {
      showBlockingWarning('Please select a market before saving.');
      return 'blocked';
    }
    if (isAnyElementContentInvalid) {
      showBlockingWarning(
        'One or more of your elements are invalid. Please update the content before saving.',
      );
      return 'blocked';
    }
    // All new templates created outside testbed must have a business entity selected
    if (
      !businessEntityId &&
      // assume testbed is hosted on adflow-testbed or localhost:3001
      !(
        window.location.href.includes('adflow-testbed') ||
        window.location.href.includes('localhost:3001')
      )
    ) {
      showBlockingWarning('Please select an advertiser before saving.');
      return 'blocked';
    }

    setShowWarningForContentTemplateLink(validateContentTemplateLinks());

    if (name === 'New Ad Template' || name.trim() === '') {
      setBgColour('red.100');
      showBlockingWarning('Please update the template name before saving.');
      return 'blocked';
    }

    if (
      (window.location.href.includes('adflow-testbed') ||
        window.location.href.includes('localhost:3001')) &&
      !brand
    ) {
      showBlockingWarning('Please select a shadow brand before saving.');
      return 'blocked';
    }

    const invalidFonts = getInvalidFontSelections();
    if (invalidFonts.length) {
      const previewItems = invalidFonts.slice(0, 3).map((item) => {
        const safeName = item.elementName ?? 'Unnamed element';

        if (item.reason === 'missing-family') {
          return { name: safeName, detail: ' (missing font family)' };
        }

        const weight = item.fontWeight ?? 400;
        const style = item.fontStyle ?? 'normal';
        const variantLabel =
          item.fontVariant && item.fontVariant.trim() !== ''
            ? item.fontVariant
            : getVariantLabel(weight, style);
        const familyLabel = item.fontFamily ? ` — ${item.fontFamily}` : '';
        return {
          name: safeName,
          detail: `${familyLabel} (${variantLabel} missing)`,
        };
      });
      const suffix = invalidFonts.length > 3 ? `and ${invalidFonts.length - 3} more` : '';

      showBlockingWarning(
        'One or more of your selected fonts or variants are not available. Please select a different option:',
        <Box>
          <Text mb={2}>
            One or more of your selected fonts or variants are not available. Please select a
            different option:
          </Text>
          <Box as="ul" pl={4}>
            {previewItems.map((item, index) => (
              <Box as="li" key={index}>
                <Text as="span" fontWeight="bold">
                  {item.name}
                </Text>
                {item.detail}
              </Box>
            ))}
            {suffix && (
              <Box as="li" listStyleType="none" mt={1}>
                <Text fontStyle="italic">{suffix}</Text>
              </Box>
            )}
          </Box>
        </Box>,
      );
      return 'blocked';
    }
    if (!validateElementContents()) {
      const mappingWarning = buildInvalidContentMappingWarning();
      showBlockingWarning(
        mappingWarning
          ? 'Custom asset mappings do not match the selected data source.'
          : 'One or more of your elements are invalid. Please update the content strings before saving.',
        mappingWarning ?? undefined,
      );
      return 'blocked';
    }
    setConfirmationOpen(true);
    return 'dialog';
  };
  openSaveRef.current = handleSaveConfirmOpen;
  return (
    <Flex
      px={2}
      py={3}
      borderWidth={1}
      borderLeftWidth={0}
      width={`calc(100% - 2 * ${spacingTwo})`}
    >
      <EditableHeading
        name={name}
        setName={setName}
        bgColour={bgColour}
        setBgColour={setBgColour}
      />
      <Flex justifyContent="flex-end" flex={1} gap={4}>
        <CancelConfirmation onClose={onClose} onOpen={onOpen} isOpen={isOpen} />
        {saveButtonVisible && (
          <Button
            isLoading={isLoading}
            testId="save-button-topbar"
            muiButtonProps={{
              onClick: handleSaveConfirmOpen,
              color: 'primary',
            }}
          >
            Save
          </Button>
        )}
        <SaveConfirmation
          isOpen={isConfirmationOpen}
          onClose={handleConfirmationClose}
          onConfirm={handleSaveConfirm}
          isLoading={isLoading}
          showWarningForContentTemplateLink={showWarningForContentTemplateLink}
        />
        <WarningModal isOpen={isWarningOpen} onClose={handleWarningClose} warning={warning} />
      </Flex>
    </Flex>
  );
};

Topbar.displayName = 'Topbar';
export default Topbar;
